// ===========================================
// Onboarding React Query Hooks
// Student Onboarding System
// ===========================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import * as onboardingApi from '../api/onboarding.api';
import type {
  OnboardingStatus,
  StartOnboardingInput,
  StepCompletionData,
} from '../api/onboarding.api';

// ===========================================
// QUERY KEYS
// ===========================================

export const onboardingKeys = {
  all: ['onboarding'] as const,
  progress: (schoolSlug: string, progressId: string) =>
    [...onboardingKeys.all, 'progress', schoolSlug, progressId] as const,
  steps: (schoolSlug: string, progressId: string) =>
    [...onboardingKeys.all, 'steps', schoolSlug, progressId] as const,
  publicLessons: (schoolSlug: string, filters?: Record<string, string | undefined>) =>
    [...onboardingKeys.all, 'public-lessons', schoolSlug, filters] as const,
  adminList: (filters?: Record<string, unknown>) =>
    [...onboardingKeys.all, 'admin-list', filters] as const,
  adminCounts: () => [...onboardingKeys.all, 'admin-counts'] as const,
  adminDetail: (id: string) => [...onboardingKeys.all, 'admin-detail', id] as const,
};

// ===========================================
// PUBLIC HOOKS (Registration wizard)
// ===========================================

/**
 * Get onboarding progress (for resuming a session)
 */
export function useOnboardingProgress(schoolSlug: string, progressId: string) {
  return useQuery({
    queryKey: onboardingKeys.progress(schoolSlug, progressId),
    queryFn: () => onboardingApi.getOnboardingProgress(schoolSlug, progressId),
    enabled: !!schoolSlug && !!progressId,
  });
}

/**
 * Get available steps for the onboarding session
 */
export function useOnboardingSteps(schoolSlug: string, progressId: string) {
  return useQuery({
    queryKey: onboardingKeys.steps(schoolSlug, progressId),
    queryFn: () => onboardingApi.getOnboardingSteps(schoolSlug, progressId),
    enabled: !!schoolSlug && !!progressId,
  });
}

/**
 * Start a new onboarding session
 */
export function useStartOnboarding(schoolSlug: string) {
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: StartOnboardingInput) =>
      onboardingApi.startOnboarding(schoolSlug, data),
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to start registration', {
        variant: 'error',
      });
    },
  });
}

/**
 * Complete a step in the onboarding flow
 */
export function useCompleteOnboardingStep(schoolSlug: string, progressId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ step, data }: { step: string; data: StepCompletionData }) =>
      onboardingApi.completeOnboardingStep(schoolSlug, progressId, step, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: onboardingKeys.progress(schoolSlug, progressId),
      });
      queryClient.invalidateQueries({
        queryKey: onboardingKeys.steps(schoolSlug, progressId),
      });
    },
    onError: (error: Error) => {
      // Don't show snackbar for step errors — handle in component
      console.error('Step completion error:', error.message);
    },
  });
}

/**
 * Fetch publicly-available lessons for the class selection step
 */
export function usePublicLessons(
  schoolSlug: string,
  filters?: { instrumentId?: string; dayOfWeek?: string }
) {
  return useQuery({
    queryKey: onboardingKeys.publicLessons(schoolSlug, filters as Record<string, string | undefined>),
    queryFn: () => onboardingApi.getPublicLessons(schoolSlug, filters),
    enabled: !!schoolSlug,
    staleTime: 30_000, // 30 s — lesson list rarely changes mid-session
  });
}

/**
 * Add another student to multi-child registration
 */
export function useAddAnotherStudent(schoolSlug: string, progressId: string) {
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: {
      studentFirstName: string;
      studentLastName: string;
      studentDateOfBirth: string;
    }) => onboardingApi.addAnotherStudent(schoolSlug, progressId, data),
    onSuccess: () => {
      enqueueSnackbar('Student added to registration', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to add student', { variant: 'error' });
    },
  });
}

// ===========================================
// ADMIN HOOKS
// ===========================================

/**
 * List onboarding records for admin dashboard
 */
export function useOnboardingList(filters?: {
  status?: OnboardingStatus;
  search?: string;
  page?: number;
  limit?: number;
}) {
  return useQuery({
    queryKey: onboardingKeys.adminList(filters as Record<string, unknown>),
    queryFn: () => onboardingApi.listOnboardingAdmin(filters),
  });
}

/**
 * Get onboarding status counts for dashboard widgets (the route needs meet_and_greet.read, #272)
 */
export function useOnboardingCounts(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: onboardingKeys.adminCounts(),
    queryFn: onboardingApi.getOnboardingCountsAdmin,
    enabled: options.enabled ?? true,
  });
}

/**
 * Get single onboarding record (admin view)
 */
export function useOnboardingDetail(id: string) {
  return useQuery({
    queryKey: onboardingKeys.adminDetail(id),
    queryFn: () => onboardingApi.getOnboardingAdmin(id),
    enabled: !!id,
  });
}

/**
 * Approve onboarding application
 */
export function useApproveOnboarding() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: onboardingApi.approveOnboardingAdmin,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
      enqueueSnackbar('Student approved', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to approve', { variant: 'error' });
    },
  });
}

/**
 * Reject onboarding application
 */
export function useRejectOnboarding() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      onboardingApi.rejectOnboardingAdmin(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
      enqueueSnackbar('Student rejected', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to reject', { variant: 'error' });
    },
  });
}

/**
 * Re-open rejected onboarding application
 */
export function useReopenOnboarding() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: onboardingApi.reopenOnboardingAdmin,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
      enqueueSnackbar('Onboarding reopened', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to reopen', { variant: 'error' });
    },
  });
}
